import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  createTableColumn,
  useTableFeatures,
  useTableColumnSizing_unstable,
} from "@fluentui/react-components";
import TableHeaderContainer from "../../../Containers/RecordingsManagement/TableContainer/TableHeaderContainer";
import TableBodyContainer from "../../../Containers/RecordingsManagement/TableContainer/TableBodyContainer";
import ColumnManager from "./ColumnManager";
import "./CSS/TableComponent.css";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";

const TableComponent = ({ data, filters, onFilterChange, columns, onColumnsChange, callFlows, supervisedAgentDetails }) => {
  const [filteredItems, setFilteredItems] = useState([]);
  const [isAudioPlayed, setIsAudioPlayed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(8); // Default
  
  // Track if any audio is playing to adjust Recording column width
  const hasAudioPlaying = useMemo(() => {
    return isAudioPlayed && typeof isAudioPlayed === 'object' && Object.values(isAudioPlayed).some(val => val === true);
  }, [isAudioPlayed]);


  // ⚡ Dynamically calculate rowsPerPage based on window height
  const calculateRowsPerPage = () => {
    const windowHeight = window.innerHeight;
    const headerHeight = 60; // Table header height
    const paginationHeight = 80; // Pagination height
    const otherSpacing = 100; // Other margins and spacing
    const rowHeight = 35; // Single row height

    const availableHeight = windowHeight - headerHeight - paginationHeight - otherSpacing;
    const calculatedRows = Math.floor(availableHeight / rowHeight);
    
    setRowsPerPage(calculatedRows > 5 ? calculatedRows : 5);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      calculateRowsPerPage();
    }, 100);
    
    window.addEventListener("resize", calculateRowsPerPage);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", calculateRowsPerPage);
    };
  }, []);

  // Columns - based on user configuration
  const tableColumns = useMemo(() => {
    const columnMapping = {
      audio: "Recording",
      date: "date",
      direction: "Direction",
      recordingScope: "recordingScope",
      agentName: "agentName",
      callerId: "callerId",
      duration: "duration",
      transcription: "callTrancription",
      callId: "callId",
    };

    return columns
      .filter((col) => col.visible)
      .map((col) => createTableColumn({ columnId: columnMapping[col.key] }));
  }, [columns]);

  // Calculate column width
  const calculateColumnWidth = (columnId, data) => {
    if (data && data.length > 0) {
      let maxLength = Math.max(
        ...data.map((item) => (item[columnId] ? item[columnId].length : 0)),
        columnId.length
      );
      return Math.max(maxLength * 10, 150);
    }
  };

  const columnSizingOptions = useMemo(() => {
    const sizingOptions = {
      Recording: {
        minWidth: hasAudioPlaying ? 300 : 80,
        idealWidth: hasAudioPlaying ? 350 : 100,
      },
      Direction: {
        minWidth: 120,
        idealWidth: 150,
      },
      recordingScope: {
        minWidth: 150,
        idealWidth: 200,
      },
      callerId: {
        minWidth: 150,
        idealWidth: 200,
      },
      duration: {
        minWidth: 150,
        idealWidth:200,
      },
      agentName: {
        minWidth: 150,
        idealWidth: 200,
      },
      date: {
        minWidth: 180,
        idealWidth: 200,
      },
      callTrancription: {
        minWidth: 120,
        idealWidth: 150,
      },
      callId: {
        minWidth: 150,
        idealWidth: 150,
      },
    };
    return sizingOptions;
  }, [hasAudioPlaying]);

  const { getRows, columnSizing_unstable, tableRef } = useTableFeatures(
    { columns: tableColumns, items: filteredItems },
    [
      useTableColumnSizing_unstable({
        columnSizingOptions,
        autoFitColumns: false,
      }),
    ]
  );

  // Pagination handler
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const rows = getRows();
  const currentRows = rows.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      <ColumnManager columns={columns} onColumnsChange={onColumnsChange} />
      <div className="recording-table-container">
        <div className="recording-scroll-table">
        <Table
          ref={tableRef}
          {...columnSizing_unstable.getTableProps()}
          noNativeElements={true}
          size="small"
          aria-label="Filtered Recordings Table"
        >
          <TableHeaderContainer
            onFilterChange={onFilterChange}
            filters={filters}
            isAudioPlayed={isAudioPlayed}
            columnSizing_unstable={columnSizing_unstable}
            columns={columns}
            callFlows={callFlows}
            supervisedAgentDetails={supervisedAgentDetails}
          />
          <TableBodyContainer
            data={data}
            setIsAudioPlayed={setIsAudioPlayed}
            isAudioPlayed={isAudioPlayed}
            setFilteredItems={setFilteredItems}
            filteredItems={filteredItems}
            currentRows={currentRows}
            columnSizing_unstable={columnSizing_unstable}
            columns={columns}
          />
        </Table>
      </div>
      </div>
      {filteredItems.length > 0 && (
        <Stack spacing={2} alignItems="center" className="recording-pagination-container">
          <Pagination
            count={Math.ceil(filteredItems.length / rowsPerPage)}
            color="primary"
            page={currentPage}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
          />
        </Stack>
      )}
    </>
  );
};

export default TableComponent;
